import { call, put, select } from 'redux-saga/effects';

import { updatePatient } from 'connect/actions';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { selectDataPatient } from 'connect/selectors';
import {
  apiGetEmailVerificationCode as apiRequestEmailVerification,
  apiGetPhoneVerificationCode as apiRequestPhoneVerification,
  apiGetPhoneVerificationSmsStatus as apiRequestPhoneVerificationSmsStatus,
  apiVerifyEmail,
  apiVerifyPhone,
} from 'endpoints/patient-endpoints';

import { SagaHandler } from '../SagaHandler';

/*
actionName and stateKeys specified in this file collide with the legacy actions and state keys.
When we kill the legacy checkout, we should rename these actions and state keys to remove the checkout prefix
*/

class _RequestEmailVerification extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/CHECKOUT_REQUEST_EMAIL_VERIFICATION',
      stateKey: 'checkoutRequestPatientEmailVerification',
    });
  }

  createAction() {
    return {
      type: this._actionName,
    };
  }

  * saga() {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);
    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      yield call(apiRequestEmailVerification, patient.id);
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'RequestEmailVerification');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _VerifyEmail extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/CHECKOUT_VERIFY_EMAIL',
      stateKey: 'checkoutVerifyEmail',
    });
  }

  createAction(code) {
    return {
      type: this._actionName,
      code,
    };
  }

  * saga({ code }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);
    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const updatedPatient = yield call(apiVerifyEmail, patient.id, code);
      yield put(updatePatient(updatedPatient));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'VerifyEmail');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _RequestPhoneVerification extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/CHECKOUT_REQUEST_PHONE_VERIFICATION',
      stateKey: 'checkoutRequestMobilePhoneVerification',
    });
  }

  createAction(method = 'text') {
    return {
      type: this._actionName,
      method,
    };
  }

  * saga({ method }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);

    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      yield call(apiRequestPhoneVerification, patient.id, method);
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'RequestPhoneVerification');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _VerifyPhone extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/CHECKOUT_VERIFY_PHONE',
      stateKey: 'checkoutVerifyPhone',
    });
  }

  createAction(code) {
    return {
      type: this._actionName,
      code,
    };
  }

  * saga({ code }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);
    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const updatedPatient = yield call(apiVerifyPhone, patient.id, code);
      yield put(updatePatient(updatedPatient));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'VerifyPhone');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _RequestPhoneVerificationSmsStatus extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/CHECKOUT_REQUEST_PHONE_VERIFICATION_SMS_STATUS',
      stateKey: 'checkoutPhoneVerificationSmsStatus',
    });
  }

  createAction() {
    return {
      type: this._actionName,
    };
  }

  * saga() {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;
    const valueHandler = this.value;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);
    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(valueHandler.update(null));

    try {
      const smsStatus = yield call(apiRequestPhoneVerificationSmsStatus, patient.id);
      yield put(valueHandler.update(smsStatus));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'RequestPhoneVerificationSmsStatus');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const RequestEmailVerification = new _RequestEmailVerification();
export const VerifyEmail = new _VerifyEmail();
export const RequestPhoneVerification = new _RequestPhoneVerification();
export const VerifyPhone = new _VerifyPhone();
export const RequestPhoneVerificationSmsStatus = new _RequestPhoneVerificationSmsStatus();
